/**
 * Base Swiper Class
 * Optimization: Used DOMContentLoaded instead of 'load' event.
 */
class InstanceSwiper extends HTMLElement {
  constructor() {
    super();
    this.handle = this.getAttribute('handle');
    this.swiperInitialized = false; // Prevent multiple initializations
  }

  static get observedAttributes() {
    return ['handle'];
  }

  init() {
    // Stop if already initialized or element is not visible
    if (this.swiperInitialized) return;

    // Never initialize on a detached element: in the editor a section can
    // reload before the fallback timeout fires, which would otherwise create
    // an orphaned Swiper that never gets destroyed.
    if (!this.isConnected) return;

    // Skip initialization if element is not visible (has no dimensions)
    if (this.offsetWidth === 0 && this.offsetHeight === 0 && !this.checkVisibility()) return;

    this.swiperInitialized = true;

    this.options = this.setOptions();
    this.instance = new Swiper('.swiper--' + this.handle, this.options);
    this.setInteractions();
  }

  /**
   * Checks if the element is visible
   */
  checkVisibility() {
    return !!(this.offsetWidth || this.offsetHeight || this.getClientRects().length);
  }

  connectedCallback() {
    if (Shopify.designMode) {
      // Keep bound refs so disconnectedCallback can detach them; otherwise these
      // window listeners accumulate on every section reload in the theme editor.
      this._onSectionEvent = () => this.init();
      window.addEventListener('shopify:section:load', this._onSectionEvent);
      window.addEventListener('shopify:section:select', this._onSectionEvent);

      // Fallback for Design mode. Store the id so disconnectedCallback can
      // clear it, otherwise a fast section reload leaves a pending timeout
      // that creates an orphaned Swiper on a detached node.
      this._fallbackInitTimeout = setTimeout(() => {
        if (!this.swiperInitialized) this.init();
      }, 100);
    } else {
      // OPTIMIZATION: Not waiting for window 'load'. Starts immediately if DOM is ready.
      if (document.readyState === 'loading') {
        this._onDomReady = () => this.init();
        document.addEventListener('DOMContentLoaded', this._onDomReady);
      } else {
        this.init();
      }
    }

    // Some gallery layouts render this slider hidden (e.g. the mobile slider is
    // display:none on desktop with the grid layout). A hidden element has no
    // dimensions, so init() bails out and the underlying .swiper stays at
    // opacity:0. Without this, resizing desktop -> mobile would reveal an
    // uninitialized (invisible) slider and the images "disappear". A debounced
    // resize handler initializes the slider the moment it becomes visible and
    // refreshes the layout of an already-initialized one.
    this._onResize = () => {
      clearTimeout(this._resizeTimeout);
      this._resizeTimeout = setTimeout(() => this.handleResize(), 150);
    };
    window.addEventListener('resize', this._onResize);
  }

  /**
   * Initialize the slider as soon as it becomes visible, or refresh an
   * already-initialized instance after a viewport change.
   */
  handleResize() {
    if (!this.swiperInitialized) {
      this.init();
    } else if (this.instance && typeof this.instance.update === 'function') {
      this.instance.update();
    }
  }

  disconnectedCallback() {
    if (this._fallbackInitTimeout) {
      clearTimeout(this._fallbackInitTimeout);
      this._fallbackInitTimeout = null;
    }
    if (this._onSectionEvent) {
      window.removeEventListener('shopify:section:load', this._onSectionEvent);
      window.removeEventListener('shopify:section:select', this._onSectionEvent);
    }
    if (this._onDomReady) document.removeEventListener('DOMContentLoaded', this._onDomReady);
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
      clearTimeout(this._resizeTimeout);
    }
    if (this.instance && this.instance.destroy) this.instance.destroy();
  }

  /**
   * Settings before initialization
   */
  setOptions() {
    this._options = this.getOptionsAsJsonScripts();
    return this._options;
  }

  getOptionsAsJsonScripts() {
    const jsonScriptAutomated = this.querySelector(`script#swiper--${this.handle}--automated-options`);
    const jsonScriptOverwrite = this.querySelector(`script#swiper--${this.handle}--overwrite-options`);
    let options = {};

    const parseJSON = scriptElement => {
      if (scriptElement && scriptElement.textContent) {
        try {
          return JSON.parse(scriptElement.textContent);
        } catch (error) {
          console.error('JSON Parse Error', error, scriptElement?.textContent);
          return {};
        }
      }
      return {};
    };

    options = { ...options, ...parseJSON(jsonScriptAutomated) };
    options = { ...options, ...parseJSON(jsonScriptOverwrite) };

    return options;
  }

  /** Thumbs instance should be mounted before this instance */
  getThumbsInstance() {
    if (!this.isThumbsActive) return;
    return document.querySelector(`[handle="${this.getAttribute('thumbs')}"]`)?.instance;
  }

  /**
   * Interactions after initialization
   */
  setInteractions() {
    // console.log('setInteractions');
  }
}

/**
 * Product Thumbnails Class
 * Optimization: Used ResizeObserver instead of setTimeout(2500).
 */
class InstanceSwiperProductThumbs extends InstanceSwiper {
  constructor() {
    super();
  }

  setOptions() {
    super.setOptions();
    this.setCenteredSlides();
    return this._options;
  }

  setCenteredSlides() {
    if (this.handle !== 'product-thumbs') return;

    const afterInit = swiper => {
      swiper.slideTo(swiper.activeIndex);
      const swiperWrapper = swiper.wrapperEl;

      const observer = new MutationObserver(resetSwiperWrapperTransform);
      observer.observe(swiperWrapper, { attributes: true });

      function resetSwiperWrapperTransform() {
        if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform !== 'translate3d(0px, 0px, 0px)'
        ) {
          swiperWrapper.style.transform = 'translate3d(0px, 0px, 0px)';
        } else if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
        ) {
          setTimeout(() => {
            if (
              swiperWrapper.hasAttribute('style') &&
              swiperWrapper.style?.transform &&
              swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
            ) {
              observer.disconnect();
            }
          }, 500);
        }
      }

      /**
       * OPTIMIZATION: Quick fix for thumbnail dislocation issue.
       * Used ResizeObserver instead of fixed 2500ms wait.
       */
      const slideElement = this.querySelector('.swiper-slide');

      const calculateAndSetHeight = () => {
        if (!slideElement) return;
        const slideHeight = slideElement.clientHeight;
        // Do not calculate if not rendered (is 0)
        if (!slideHeight) return;

        const calculateAllThumbHeight = (slideHeight + 16) * this.querySelectorAll('.swiper-slide').length;
        const thumbsWrapper = this.parentElement;

        if (thumbsWrapper.offsetHeight === calculateAllThumbHeight || calculateAllThumbHeight <= thumbsWrapper.offsetHeight) return;

        const calculatedHeight = calculateAllThumbHeight - 16 + 'px';

        if (thumbsWrapper.offsetHeight > calculateAllThumbHeight) {
          thumbsWrapper.style.height = calculatedHeight;
        } else {
          thumbsWrapper.removeAttribute('style');
        }
      };

      // Initial run
      calculateAndSetHeight();

      // Listen for changes (CSS loading, screen size change, etc.)
      const resizeObserver = new ResizeObserver(() => {
        calculateAndSetHeight();
      });

      // Observe parent element size changes
      if (this.parentElement) {
        resizeObserver.observe(this.parentElement);
      }
    };

    this._options = {
      ...this._options,
      on: {
        ...(this._options.on || {}),
        afterInit: afterInit
      }
    };
  }
}

if (!customElements.get('swiper-product-thumbs')) {
  customElements.define('swiper-product-thumbs', InstanceSwiperProductThumbs);
}

/**
 * Main Product Gallery Class
 */
class InstanceSwiperProductGallery extends InstanceSwiper {
  constructor() {
    super();
    this.isThumbsActive = this.hasAttribute('thumbs');
    this.isZoomActive = this.hasAttribute('zoom');
    this.modelViewerBtn = this.querySelector('.model-viewer-btn');
    this.modelViewer = this.querySelector('model-viewer');
    this.isModelActive = false;
  }

  static get observedAttributes() {
    return ['thumbs', 'zoom'];
  }

  setOptions() {
    super.setOptions();
    this.setThumbOptions();
    return this._options;
  }

  setThumbOptions() {
    if (!this.isThumbsActive) return;
    let thumbsInstance = super.getThumbsInstance();

    // When the gallery is initialized on resize (e.g. switching from the
    // desktop grid to the mobile slider), the thumbs slider may not be
    // initialized yet. Force it first so the two stay in sync.
    if (!thumbsInstance) {
      const thumbsEl = document.querySelector(`[handle="${this.getAttribute('thumbs')}"]`);
      if (thumbsEl && typeof thumbsEl.init === 'function') {
        thumbsEl.init();
        thumbsInstance = super.getThumbsInstance();
      }
    }

    this._options = {
      ...this._options,
      thumbs: {
        swiper: thumbsInstance
      }
    };
  }

  /**
   * Interactions after initialization
   */
  setInteractions() {
    if (this.isThumbsActive) this.setThumbsInteraction();
    if (this.isZoomActive) this.photoSwipeLightboxInit();
    this.initModelViewer();

    // Add thumb click handler for model slides
    const thumbSlides = document.querySelectorAll('.swiper-slide[data-media-type="model"]');
    thumbSlides.forEach(slide => {
      slide.addEventListener('click', () => this.enableSwiper());
    });

    // Force pagination update on mobile after each slide change
    this.instance.on('slideChange', swiper => {
      if (DeviceDetector.isMobile()) {
        swiper.pagination.update();
      }
    });

    // Detect scroll event and force update pagination when scrolling ends
    this.instance.on('scroll', swiper => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        swiper.pagination.update();
      }, 200);
    });
  }

  initModelViewer() {
    // Find all swiper slides with model viewers
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    this.resetAllModelViewers();
    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const container = slide.querySelector('.model-viewer-container');
      const modelViewer = container?.querySelector('model-viewer');

      if (btn && container && modelViewer) {
        // Store references to associated elements
        btn.modelViewer = modelViewer;
        modelViewer.controlButton = btn;

        // Model viewer container click handler
        container.addEventListener('click', e => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
            btn.click();
          }
        });

        // Model viewer button click handler
        btn.addEventListener('click', e => {
          e.preventDefault();
          this.toggleModelViewer(btn, modelViewer);
        });

        // Prevent model-viewer direct interaction
        modelViewer.addEventListener('mousedown', e => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
          }
        });
      }
    });

    // Add navigation click handlers
    if (this.instance?.navigation) {
      const { nextEl, prevEl } = this.instance.navigation;
      if (nextEl) {
        nextEl.addEventListener('click', () => {
          this.resetAllModelViewers();
          this.enableSwiper(true);
        });
      }
      if (prevEl) {
        prevEl.addEventListener('click', () => {
          this.resetAllModelViewers();
          this.enableSwiper(true);
        });
      }
    }
  }

  resetAllModelViewers() {
    // Find all model viewers in the current gallery instance
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const modelViewer = slide.querySelector('model-viewer');

      if (btn && modelViewer) {
        btn.classList.remove('is-active');
        modelViewer.interactionPrompt = 'none';
        modelViewer.cameraControls = false;
      }
    });

    this.isModelActive = false;
    this.instance && (this.instance.allowTouchMove = true);
  }

  toggleModelViewer(btn, modelViewer) {
    const wasActive = btn.classList.contains('is-active');

    // Reset all model viewers in this gallery
    this.resetAllModelViewers();

    if (!wasActive) {
      this.isModelActive = true;
      this.instance.allowTouchMove = false;

      // Enable only this specific model viewer
      modelViewer.interactionPrompt = 'auto';
      modelViewer.cameraControls = true;
      btn.classList.add('is-active');
    }
  }

  temporarilyDisableSwiper() {
    // Only disable touch/mouse dragging
    this.instance.allowTouchMove = false;

    // Re-enable after a short delay if no model interaction occurs
    this.swiperTimeout = setTimeout(() => {
      if (!this.isModelActive) {
        this.enableSwiper();
      }
    }, 100);
  }

  enableSwiper(force = false) {
    if (this.swiperTimeout) {
      clearTimeout(this.swiperTimeout);
    }

    // Always enable touch/mouse dragging if force is true or model is not active
    if (force || !this.isModelActive) {
      this.instance.allowTouchMove = true;

      // Reset model viewer state if forced
      if (force && this.isModelActive) {
        this.isModelActive = false;
        if (this.modelViewer) {
          this.modelViewer.interactionPrompt = 'none';
          this.modelViewer.cameraControls = false;
        }
        this.modelViewerBtn.classList.remove('is-active');
      }
    }
  }

  setThumbsInteraction() {
    if (!this.isThumbsActive || !this.instance) return;
    const thumbsSwiper = this.options.thumbs.swiper;

    // Enable swiper on thumb click
    thumbsSwiper.el.addEventListener('click', () => {
      this.enableSwiper(true);
      if (this.modelViewerBtn) {
        this.modelViewerBtn.classList.remove('is-active');
      }
    });

    this.instance.on('slideChangeTransitionStart', function (swiper) {
      const activeIndex = swiper.activeIndex;
      const thumbsActiveIndex = thumbsSwiper.activeIndex;
      if (activeIndex !== thumbsActiveIndex) {
        thumbsSwiper.slideTo(activeIndex);
      }
    });
  }

  photoSwipeLightboxInit() {
    // Delegated to a standalone, idempotent initializer so the lightbox works
    // regardless of which gallery layout (slider/grid/single) triggers it and
    // even when the zoom-bearing slider is hidden (e.g. grid layout on desktop).
    initProductPhotoSwipe();
    initProductVideoToggles();
  }

  setActiveMedia(id) {
    const mediaFound = Array.from(this.querySelectorAll('[data-media-id]')).find(media => Number(media.dataset.mediaId) === id);

    if (!mediaFound) return;

    if (this.instance && typeof this.instance.slideTo === 'function') {
      if (this.dataset.hideOtherVariantsMedia === 'false') {
        this.instance.slideTo(Number(mediaFound.dataset.index));
      }
    }
  }
}
if (!customElements.get('swiper-product-gallery')) {
  customElements.define('swiper-product-gallery', InstanceSwiperProductGallery);
}

/**
 * Standalone PhotoSwipe initializer for the product media gallery.
 *
 * Why standalone: the lightbox must intercept clicks on every
 * `.photoswipe-wrapper a.product__gallery-toggle` link (otherwise the link's
 * `target="_blank"` opens the image in a new tab). Initializing it from inside
 * the swiper instance was fragile: it only ran when a gallery with the `zoom`
 * attribute was both present AND visible, so it failed for the grid layout on
 * desktop (the zoom-bearing slider is hidden) and for single-media products
 * (no swiper at all). Binding once at the document level fixes every case.
 *
 * Idempotent: PhotoSwipe's `gallery` selector already targets all wrappers on
 * the page, so a single instance is enough. The guard prevents double binding
 * if multiple gallery instances also call it.
 */
function initProductPhotoSwipe() {
  if (window.__productPhotoSwipeInitialized) return;

  // Only relevant when zoomable links are actually rendered (zoom !== 'none').
  const hasToggle = document.querySelector('.photoswipe-wrapper a.product__gallery-toggle');
  if (!hasToggle) return;

  // Bail if the PhotoSwipe libraries haven't loaded yet.
  if (typeof PhotoSwipeLightbox === 'undefined' || typeof PhotoSwipe === 'undefined') return;

  window.__productPhotoSwipeInitialized = true;

  const [closeIcon, prevIcon, nextIcon] = [
    document.querySelector('[data-close-icon]'),
    document.querySelector('[data-prev-icon]'),
    document.querySelector('[data-next-icon]')
  ];

  const photoSwipeLightboxInstance = new PhotoSwipeLightbox({
    gallery: `.photoswipe-wrapper`,
    children: 'a.product__gallery-toggle',
    mainClass: 'pswp--product-media-gallery',

    loop: false,
    // Instant, ultra-smooth open/close: fade only (no full-res transform), with
    // very short durations so it feels immediate while staying GPU-accelerated.
    showHideAnimationType: 'fade',
    showAnimationDuration: 180,
    hideAnimationDuration: 150,

    initialZoomLevel: zoomLevelObject => {
      if (DeviceDetector.isMobile()) {
        return zoomLevelObject.vFill;
      } else {
        return zoomLevelObject.fit;
      }
    },
    secondaryZoomLevel: zoomLevelObject => {
      if (DeviceDetector.isMobile()) {
        return zoomLevelObject.fit;
      } else {
        return 1;
      }
    },
    pswpModule: PhotoSwipe
  });

  photoSwipeLightboxInstance.addFilter('uiElement', (element, data) => {
    if (data.name === 'close' && closeIcon) {
      element.innerHTML = closeIcon.innerHTML;
    } else if (data.name === 'arrowPrev' && prevIcon) {
      element.innerHTML = prevIcon.innerHTML;
    } else if (data.name === 'arrowNext' && nextIcon) {
      element.innerHTML = nextIcon.innerHTML;
    }
    return element;
  });

  // html for video
  photoSwipeLightboxInstance.addFilter('itemData', (itemData) => {
    const element = itemData.element;
    if (!element) return itemData;

    if (element.dataset.pswpVideoSrc) {
      const src = element.dataset.pswpVideoSrc.replace(/"/g, '&quot;');
      return {
        element,
        type: 'html',
        width: Number(element.dataset.pswpWidth) || 1920,
        height: Number(element.dataset.pswpHeight) || 1080,
        html: `<video src="${src}" playsinline muted loop controls autoplay></video>`
      };
    }

    if (itemData.type === 'html' && element.dataset.pswpHtml) {
      return {
        ...itemData,
        html: element.dataset.pswpHtml,
        width: Number(element.dataset.pswpWidth) || itemData.width || 1920,
        height: Number(element.dataset.pswpHeight) || itemData.height || 1080
      };
    }

    return itemData;
  });

  photoSwipeLightboxInstance.init();

  photoSwipeLightboxInstance.on('beforeOpen', () => {
    document.body.classList.add('overflow-hidden');
    document.querySelectorAll('.main-product__media video').forEach((video) => {
      video.pause();
    });
  });

  const playLightboxVideo = (content) => {
    const video = content?.element?.querySelector?.('video');
    if (!video) return;
    video.controls = true;
    video.muted = true;
    video.play().catch(() => {});
  };

  photoSwipeLightboxInstance.on('contentActivate', ({ content }) => {
    playLightboxVideo(content);
  });

  photoSwipeLightboxInstance.on('contentDeactivate', ({ content }) => {
    content?.element?.querySelector?.('video')?.pause();
  });

  photoSwipeLightboxInstance.on('closingAnimationStart', () => {
    document.body.classList.remove('overflow-hidden');
    document.querySelectorAll('.pswp video').forEach((video) => video.pause());
  });

  photoSwipeLightboxInstance.on('close', () => {
    document.querySelectorAll('.main-product__media video.product-media-video').forEach((video) => {
      video.play().catch(() => {});
    });
  });
}

function initProductVideoToggles() {
  document.querySelectorAll('[data-video-toggle]').forEach((button) => {
    if (button.dataset.bound === 'true') return;

    const media = button.closest('[data-media-type="video"]');
    const video = media?.querySelector('video');
    if (!video) return;

    button.dataset.bound = 'true';

    const syncState = () => {
      button.classList.toggle('is-playing', !video.paused);
    };

    video.addEventListener('play', syncState);
    video.addEventListener('pause', syncState);
    syncState();

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  });
}

// Fallback: initialize even if no visible zoom-enabled swiper triggers it
// (grid layout on desktop, single-media products, etc.).
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initProductPhotoSwipe();
    initProductVideoToggles();
  });
} else {
  initProductPhotoSwipe();
  initProductVideoToggles();
}

/**
 * Product Media Info Class
 */
class ProductMediaInfo extends HTMLElement {
  constructor() {
    super();

    // Bind once so the same reference can be removed in disconnectedCallback.
    this._boundInit = this.init.bind(this);

    this.init();
    window.addEventListener('resize', this._boundInit);

    if (Shopify.designMode) {
      window.addEventListener('shopify:section:load', this._boundInit);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._boundInit);
    if (Shopify.designMode) {
      window.removeEventListener('shopify:section:load', this._boundInit);
    }
  }

  init() {
    let containerOffsetWidth = document.querySelector('.main-product__media--slider')?.offsetWidth || 330;
    if (document.querySelector('.main-product__media--grid-item') && !DeviceDetector.isMobile()) {
      const gridItem = document.querySelector('.main-product__media--grid-item');
      if (gridItem) containerOffsetWidth = gridItem.offsetWidth;
    }
    const maxWidthForInfo = (containerOffsetWidth - 48) / 2;

    const lastP = this.querySelector('p:last-child');
    if (!lastP) return;

    const mediaInfoTextWidth = lastP.offsetWidth;
    const mediaInfoHidden = this.querySelector('p[aria-hidden]');

    if (mediaInfoTextWidth > maxWidthForInfo) {
      this.classList.remove('animation-stopped');
      this.style.cssText = `--marquee-speed: ${(mediaInfoTextWidth / maxWidthForInfo) * 8}s`;
      if (mediaInfoHidden) mediaInfoHidden.style.display = '';
    } else {
      this.classList.add('animation-stopped');
      this.style.cssText = '';
      if (mediaInfoHidden) mediaInfoHidden.style.display = 'none';
    }
  }
}

if (!customElements.get('product-media-info')) {
  customElements.define('product-media-info', ProductMediaInfo);
}
